import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChoiceTile, type ChoiceTileState } from '../../../ui/components/ChoiceTile/ChoiceTile.tsx';
import { optionGridStyle } from '../option-grid.ts';
import type { ExercisePhase, SubmitResult } from '../registry.ts';
import { TextChoiceFeedback, isTextChoiceFeedback } from './TextChoiceFeedback.tsx';
import styles from './TextChoiceExercise.module.css';

/**
 * `multiple_choice`'s answering surface (`../note-identification/
 * NoteIdentificationRenderer.tsx`'s layout, generalised over what sits above
 * the option grid): the option tiles, the submission lock, the number-key
 * shortcuts, and the feedback panel, with the PROMPT above them left to the
 * caller as `prompt` so a future text-answered type can reuse this without
 * copying it.
 */
interface TextChoiceExerciseProps {
  promptId: string;
  options: readonly string[];
  /** Whether an option may be picked right now. */
  answerable: boolean;
  phase: ExercisePhase;
  feedback: unknown;
  onSubmit: (response: unknown) => Promise<SubmitResult>;
  prompt: ReactNode;
}

export function TextChoiceExercise({
  promptId,
  options,
  answerable,
  phase,
  feedback,
  onSubmit,
  prompt,
}: TextChoiceExerciseProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const submissionLockRef = useRef(false);

  // A new attempt starts unlocked. Keyed on `promptId` rather than the
  // options, which can repeat verbatim between two attempts of the same
  // Exercise (`NoteIdentificationRenderer.tsx`'s reasoning applies here too).
  useEffect(() => {
    setSelectedIndex(null);
    submissionLockRef.current = false;
  }, [promptId]);

  const handleSelect = useCallback(
    (index: number) => {
      if (submissionLockRef.current || !answerable) return;
      submissionLockRef.current = true;
      setSelectedIndex(index);
      onSubmit({ selectedIndex: index }).catch(() => {
        submissionLockRef.current = false;
      });
    },
    [answerable, onSubmit],
  );

  // Number keys 1..k, gated on the same conditions the tiles enforce through
  // `disabled` — a `window` listener bypasses a disabled DOM control, so the
  // gate is checked again here.
  const gateRef = useRef({ phase, answerable, options, handleSelect });
  gateRef.current = { phase, answerable, options, handleSelect };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const { phase: currentPhase, answerable: canAnswer, options: currentOptions, handleSelect: select } =
        gateRef.current;

      if (currentPhase !== 'answering' || !canAnswer) return;
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;

      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }

      const index = Number(event.key) - 1;
      if (!Number.isInteger(index) || index < 0 || index >= currentOptions.length) return;
      select(index);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const parsedFeedback = isTextChoiceFeedback(feedback) ? feedback : null;

  function tileState(index: number): ChoiceTileState {
    if (!answerable) return 'disabled';

    if (parsedFeedback) {
      if (index === parsedFeedback.correctIndex) return 'resolved-correct';
      if (index === parsedFeedback.selectedIndex) return 'resolved-miss';
      return 'unchosen';
    }

    if (phase === 'submitting' && index === selectedIndex) return 'selected';
    return 'default';
  }

  return (
    <div className={styles.root} data-resolved={parsedFeedback ? 'true' : undefined}>
      <div className={styles.prompt}>{prompt}</div>

      <div className={styles.options} style={optionGridStyle(options.length)} data-testid="options">
        {options.map((option, index) => (
          <ChoiceTile
            key={`${index}-${option}`}
            label={option}
            keyHint={String(index + 1)}
            state={tileState(index)}
            onSelect={() => handleSelect(index)}
          />
        ))}
      </div>

      {parsedFeedback ? (
        <div className={styles.feedback}>
          <TextChoiceFeedback feedback={parsedFeedback} options={options} />
        </div>
      ) : null}
    </div>
  );
}
